import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FlowDefinition } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function FlowDefinitions() {
  const { persona } = useAuth();
  const listQ = useApiQuery<{ items: FlowDefinition[] }>('/api/v1/flow/definitions?limit=100');
  const [showCreate, setShowCreate] = useState(false);
  const canAdmin = persona?.roles.includes('FLOW_ADMIN') ?? false;

  return (
    <div>
      <PageHeader
        title="Flow definitions"
        subtitle="Active state-machine definitions powering every application/permit/case flow"
        actions={
          canAdmin ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New / update definition
            </button>
          ) : undefined
        }
      />
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No active flow definitions" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Module</th>
                <th className="table-head">Version</th>
                <th className="table-head">Status</th>
                <th className="table-head">Created</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">
                    <Link to={`/flow/definitions/${d.code}`} className="text-brand-600 underline">
                      {d.code}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{d.moduleCode}</td>
                  <td className="table-cell tabular-nums">v{d.version}</td>
                  <td className="table-cell">
                    <StatusBadge status={d.active ? 'ACTIVE' : 'INACTIVE'} />
                  </td>
                  <td className="table-cell text-slate-500">{new Date(d.createdAt).toLocaleDateString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [moduleCode, setModuleCode] = useState('');
  const [dslText, setDslText] = useState(
    '{\n  "refPrefix": "XX",\n  "initial": "SUBMITTED",\n  "states": {\n    "SUBMITTED": { "terminal": true }\n  }\n}',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      let dsl: unknown;
      try {
        dsl = JSON.parse(dslText);
      } catch {
        throw new Error('DSL must be valid JSON');
      }
      await api.post('/api/v1/flow/definitions', { code, moduleCode, dsl });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New definition / next version" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          Posting an existing code creates the next version and deactivates the previous one; a new code starts at v1.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (lowercase_snake)">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="business_licence" />
          </Field>
          <Field label="Owning module code">
            <input className="field-input" value={moduleCode} onChange={(e) => setModuleCode(e.target.value)} placeholder="m13" />
          </Field>
        </div>
        <Field label="DSL (JSON)">
          <textarea
            className="field-input font-mono text-xs"
            rows={12}
            value={dslText}
            onChange={(e) => setDslText(e.target.value)}
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code || !moduleCode}>
            {busy ? 'Saving…' : 'Save definition'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
